import { LoadingBox } from "src/components/common/LoadingBox.tsx";
import { ErrorView } from "src/components/common/errors/ErrorView.tsx";
import { AsyncState } from "src/utils/async/asyncState";
import { ReactNode } from "react";
import { HorizontalBox } from "../box/HorizontalBox";
import { SxProps, Theme } from "@mui/material/styles";
import { mergeSx } from "src/utils/styles.ts";
import { LoggedError } from "src/errorHandling/errorLogging.ts";

export interface AsyncRenderProps<TData, TReloadParams> {
	asyncData: AsyncState<TData>;
	content?: (data: TData) => ReactNode;
	noContainer?: boolean;
	containerSx?: SxProps<Theme>;
	reloadOnError?: (params?: TReloadParams) => Promise<unknown>;
	errorContent?: (props: AsyncRenderErrorContentProps<TReloadParams>) => ReactNode;
	skeleton?: ReactNode;
	errorSx?: SxProps<Theme>;
}

export interface AsyncRenderErrorContentProps<TReloadParams> {
	error: LoggedError;
	reload?: (params?: TReloadParams) => Promise<unknown>;
}

export function AsyncRender<TData, TReloadParams = undefined>({
	asyncData,
	content,
	reloadOnError,
	noContainer = false,
	containerSx,
	errorContent,
	skeleton,
	errorSx,
}: AsyncRenderProps<TData, TReloadParams>) {
	const { data, loading, error } = asyncData;

	if (error != undefined) {
		if (errorContent != null) return errorContent({ error, reload: reloadOnError });
		else
			return (
				<ErrorView
					error={error}
					reload={reloadOnError ? (params?: TReloadParams) => reloadOnError?.(params) : undefined}
					sx={errorSx}
				/>
			);
	}

	const loadingNode = skeleton ?? <LoadingBox overlay />;

	if (noContainer && data !== undefined && content !== undefined) return content(data);
	if (noContainer && data === undefined && loading) return loadingNode;

	// Render content always inside loading spinner container to avoid
	// remounts when loading state changes
	// This can be
	return (
		<HorizontalBox
			sx={mergeSx(
				{
					flex: 1,
					position: "relative",
				},
				containerSx,
			)}
		>
			{data !== undefined && content !== undefined && content(data)}
			{loading && loadingNode}
		</HorizontalBox>
	);
}
